// components/ScheduleForm.tsx
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { DeviceSchedule, CreateScheduleRequest, DAYS_OF_WEEK, validateSchedule } from '../types/schedules';
import { Device } from '../types/devices';
import { Playlist } from '../types/playlists';
import { devicesApi } from '../services/devicesApi';
import { playlistsApi } from '../services/playlistsApi';

interface ScheduleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: CreateScheduleRequest) => Promise<void>;
  schedule?: DeviceSchedule;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  isOpen,
  onClose,
  onSave,
  schedule
}) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateScheduleRequest>({
    deviceId: 0,
    playlistId: 0,
    startTime: '09:00',
    endTime: '17:00',
    daysOfWeek: 'Mon,Tue,Wed,Thu,Fri',
    priority: 1,
    active: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [devicesData, playlistsData] = await Promise.all([
          devicesApi.getAllDevices(),
          playlistsApi.getAllPlaylists()
        ]);
        setDevices(devicesData);
        setPlaylists(playlistsData);
        setError(null);
      } catch (err) {
        setError('Failed to load required data');
        console.error('Error loading form data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (schedule) {
      setFormData({
        deviceId: schedule.deviceId || 0,
        playlistId: schedule.playlistId || 0,
        startTime: schedule.startTime || '09:00',
        endTime: schedule.endTime || '17:00',
        daysOfWeek: schedule.daysOfWeek,
        priority: schedule.priority,
        active: schedule.active
      });

      setSelectedDays(new Set(schedule.daysOfWeek.split(',')));
    }
  }, [schedule]);

  const handleDayToggle = (day: string) => {
    const newDays = new Set(selectedDays);
    if (newDays.has(day)) {
      newDays.delete(day);
    } else {
      newDays.add(day);
    }
    setSelectedDays(newDays);
    setFormData({
      ...formData,
      daysOfWeek: Array.from(newDays).join(',')
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form Data:', formData);

    const validationErrors = validateSchedule(formData);
    if (validationErrors.length > 0) {
      setError(validationErrors.map(err => err.message).join(', '));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(formData);
      onClose();
    } catch (err) {
      setError('Failed to save schedule: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPreset = (preset: 'weekdays' | 'weekends' | 'everyday') => {
    let days: string[];
    switch (preset) {
      case 'weekdays':
        days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        break;
      case 'weekends':
        days = ['Sat', 'Sun'];
        break;
      case 'everyday':
        days = DAYS_OF_WEEK;
        break;
    }
    setSelectedDays(new Set(days));
    setFormData({
      ...formData,
      daysOfWeek: days.join(',')
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {schedule ? 'Edit Schedule' : 'Create Schedule'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Device Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Device
                </label>
                <select
                  value={formData.deviceId}
                  onChange={(e) => setFormData({
                    ...formData,
                    deviceId: parseInt(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                >
                  <option value="">Select a device...</option>
                  {devices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.deviceName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Playlist Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Playlist
                </label>
                <select
                  value={formData.playlistId}
                  onChange={(e) => setFormData({
                    ...formData,
                    playlistId: parseInt(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                >
                  <option value="">Select a playlist...</option>
                  {playlists.map((playlist) => (
                    <option key={playlist.playlistId} value={playlist.playlistId}>
                      {playlist.playlistName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({
                      ...formData,
                      startTime: e.target.value + ':00' // Add ':00' for seconds
                    })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({
                      ...formData,
                      endTime: e.target.value + ':00'
                    })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    required
                  />
                </div>
              </div>

              {/* Days Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days
                </label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => handleSelectPreset('weekdays')}
                    className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200"
                  >
                    Weekdays
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectPreset('weekends')}
                    className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200"
                  >
                    Weekends
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectPreset('everyday')}
                    className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200"
                  >
                    Every Day
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-4 py-2 rounded-md ${
                        selectedDays.has(day)
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      } border`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.priority}
                  onChange={(e) => setFormData({
                    ...formData,
                    priority: parseInt(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Higher priority schedules take precedence (1-100)
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({
                    ...formData,
                    active: e.target.checked
                  })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                  Schedule is active
                </label>
              </div>

              {error && (
                <div className="p-4 bg-red-50 rounded-md">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                    <span className="text-red-800">{error}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ScheduleForm;